export type ServerBackedPhase = { type: string };

export type ServerBackedStep<T> = {
  phaseType: string;
  insertAfter: string;
  step: T & { id: string };
};

/**
 * Add a required workflow step even before the API has a phase row for it.
 * The step is still backed by the cycle's accepted modality; its persisted
 * phase is created only when the patient records or skips the step.
 */
export function insertRequiredStep<T extends { id: string }>(
  steps: readonly T[],
  insertAfter: string,
  step: T,
): T[] {
  if (steps.some(({ id }) => id === step.id)) return [...steps];

  const anchorIndex = steps.findIndex(({ id }) => id === insertAfter);
  if (anchorIndex < 0) return [...steps, step];

  return [
    ...steps.slice(0, anchorIndex + 1),
    step,
    ...steps.slice(anchorIndex + 1),
  ];
}

/**
 * Add only the patient-facing steps that the cycle API actually returned.
 * The server phase list is the source of truth for conditional treatment work.
 */
export function insertServerBackedSteps<T extends { id: string }>(
  steps: readonly T[],
  phases: readonly ServerBackedPhase[],
  candidates: readonly ServerBackedStep<T>[],
): T[] {
  const phaseTypes = new Set(phases.map(({ type }) => type));
  let result = [...steps];

  for (const candidate of candidates) {
    if (
      !phaseTypes.has(candidate.phaseType) ||
      result.some(({ id }) => id === candidate.step.id)
    ) {
      continue;
    }

    const anchorIndex = result.findIndex(
      ({ id }) => id === candidate.insertAfter,
    );
    if (anchorIndex < 0) {
      result = [...result, candidate.step];
      continue;
    }

    result = [
      ...result.slice(0, anchorIndex + 1),
      candidate.step,
      ...result.slice(anchorIndex + 1),
    ];
  }

  return result;
}
